import React from 'react';
import {createGlobalStyle} from 'styled-components';
import {Transition, TransitionProps} from '../Transition';
import {getTimeouts} from '../utils';

export type GrowProps = Pick<
  TransitionProps,
  'mounted' | 'duration' | 'delay' | 'preventAnimationOnMount'
> & {
  direction?: 'up' | 'down' | 'left' | 'right' | 'center';
};

export const Grow: React.FC<React.PropsWithChildren<GrowProps>> = ({
  children,
  mounted,
  duration,
  delay,
  direction = 'left',
  preventAnimationOnMount,
}) => {
  const nodeRef = React.useRef(null);

  const timeouts = getTimeouts({duration, delay});
  const animationDuration = mounted
    ? timeouts.mountDuration
    : timeouts.unmountDuration;
  const animationDelay = mounted ? timeouts.mountDelay : timeouts.unmountDelay;

  const shouldAnimate =
    preventAnimationOnMount && mounted ? 'pause' : 'running';

  const styles = {
    transformOrigin: `center ${direction}`,
    opacity: mounted ? 0 : 1,
    transform: `scale(${mounted ? 0 : 1})`,
    animation: `${
      mounted ? 'enterGrow' : 'exitShrink'
    } ${animationDuration}ms ease-in-out ${animationDelay}ms forwards ${shouldAnimate}`,
  };

  return (
    <Transition
      nodeRef={nodeRef}
      mounted={mounted}
      duration={duration}
      delay={delay}
    >
      {React.Children.map(children, (child: any) => {
        return React.cloneElement(child, {
          style: {
            ...(child.props?.style ? child.props.style : {}),
            ...styles,
          },
          ref: nodeRef,
        });
      })}
      <Animations />
    </Transition>
  );
};

const Animations = createGlobalStyle`
  @keyframes enterGrow {
    from { opacity: 0; transform: scale(0); }
    to { opacity: 1; transform: scale(1); }
  }
  @keyframes exitShrink {
    from { opacity: 1; transform: scale(1); }
    to { opacity: 0; transform: scale(0); }
  }
`;
